import axios from 'axios';
import { bytechatUrl } from '../utils/network';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  model?: string;
  images?: string[]; // Array of base64 strings
}

export interface ChatRequest {
  prompt: string;
  model: string;
  mode?: 'llm' | 'rag' | 'agent';
  images?: string[]; // Array of base64 strings
}

export interface ChatResponse {
  response: string;
  status: string;
}

export interface StreamResponse {
  message: string;
  timestamp?: string;
}

// Additional model categories for reference (not used in chat interface)
export const HUGGINGFACE_MODELS = [
  { id: 'nomic-ai/nomic-embed-multimodal-3b', name: 'Nomic Embed Multimodal 3B', category: 'Embedding' },
  { id: 'jinaai/jina-colbert-v2', name: 'Jina ColBERT v2', category: 'Reranker' },
] as const;

export const TEXT_EMBEDDING_MODELS = [
  'nomic-ai/nomic-embed-multimodal-3b'
] as const;

export const IMAGE_EMBEDDING_MODELS = [
  'nomic-ai/nomic-embed-multimodal-3b'
] as const;

export const RERANKER_MODELS = [
  'jinaai/jina-colbert-v2'
] as const;

// Text generation models available for chat
export const AVAILABLE_MODELS = [
  // OpenAI Models
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', category: 'OpenAI', supportsImages: false },
  { id: 'gpt-4o', name: 'GPT-4o', category: 'OpenAI', supportsImages: false },
  
  // Qwen Models
  { id: 'qwen2.5-vl-72b-instruct', name: 'Qwen2.5 VL 72B Instruct (Vision)', category: 'Qwen', supportsImages: true },
  
  // DeepSeek Models
  { id: 'deepseek-chat', name: 'DeepSeek Chat', category: 'DeepSeek', supportsImages: false },
  { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner', category: 'DeepSeek', supportsImages: false },
  
  // Ollama Models - Qwen Series
  { id: 'qwen2.5:7b-instruct-q8_0', name: 'Qwen2.5 7B Instruct (Recommended)', category: 'Ollama', supportsImages: false },
  { id: 'qwen2.5vl:3b-q4_K_M', name: 'Qwen2.5 VL 3B (Vision)', category: 'Ollama', supportsImages: true },
  { id: 'qwen2.5vl:7b-q8_0', name: 'Qwen2.5 VL 7B (Vision)', category: 'Ollama', supportsImages: true },
  { id: 'qwen3:4b-q8_0', name: 'Qwen3 4B', category: 'Ollama', supportsImages: false },
  { id: 'qwen3:8b-q8_0', name: 'Qwen3 8B', category: 'Ollama', supportsImages: false },
  { id: 'qwen3:8b-fp16', name: 'Qwen3 8B FP16', category: 'Ollama', supportsImages: false },
  { id: 'qwen3:14b-q8_0', name: 'Qwen3 14B', category: 'Ollama', supportsImages: false },
  { id: 'qwen3:32b-q4_K_M', name: 'Qwen3 32B', category: 'Ollama', supportsImages: false },
  
  // Ollama Models - Other Series
  { id: 'gemma3:12b-it-q8_0', name: 'Gemma3 12B Instruct', category: 'Ollama', supportsImages: false },
  { id: 'llama3.1:8b-instruct-q8_0', name: 'Llama3.1 8B Instruct', category: 'Ollama', supportsImages: false },
  
  // DeepSeek R1 Distilled Models
  { id: 'deepseek-r1:32b-qwen-distill-q4_K_M', name: 'DeepSeek R1 32B Qwen Distill', category: 'Ollama', supportsImages: false },
  { id: 'deepseek-r1:14b-qwen-distill-q8_0', name: 'DeepSeek R1 14B Qwen Distill', category: 'Ollama', supportsImages: false },
  { id: 'deepseek-r1:8b-llama-distill-fp16', name: 'DeepSeek R1 8B Llama Distill', category: 'Ollama', supportsImages: false },
] as const;

export const sendChatMessage = async (prompt: string, model: string, mode?: 'llm' | 'rag' | 'agent', images?: string[]): Promise<string> => {
  try {
    // Get the API base URL dynamically
    const API_BASE_URL = await bytechatUrl();
    
    // If no prompt but images are provided, use a default prompt
    const finalPrompt = prompt.trim() || (images && images.length > 0 ? "Please analyze these images." : "");
    
    // Always use JSON for messages (no more FormData)
    const payload: ChatRequest = {
      prompt: finalPrompt,
      model,
      mode,
      images
    };

    console.log('API payload:', {
      prompt: finalPrompt.substring(0, 100) + '...',
      model,
      mode,
      imagesCount: images?.length || 0,
      firstImageLength: images?.[0]?.length || 0,
      firstImagePreview: images?.[0]?.substring(0, 50) + '...' || 'none'
    });

    const response = await axios.post<ChatResponse>(`${API_BASE_URL}ai/chat`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60000, // 60 second timeout for image requests
    });

    if (response.data && response.data.response) {
      return response.data.response;
    } else {
      throw new Error('Invalid response format from server');
    }
  } catch (error) {
    console.error('Error sending chat message:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - please try again');
      } else if (error.response?.status === 404) {
        throw new Error('API endpoint not found');
      } else if (error.response && error.response.status >= 500) {
        throw new Error('Server error - please try again later');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    
    throw new Error('Failed to send message - please check your connection');
  }
};

export const sendChatMessageStream = async (
  prompt: string, 
  model: string, 
  onMessage: (chunk: string) => void,
  onComplete: () => void,
  onError: (error: string) => void,
  mode?: 'llm' | 'rag' | 'agent',
  images?: string[]
): Promise<void> => {
  try {
    // Get the API base URL dynamically
    const API_BASE_URL = await bytechatUrl();
    
    // If no prompt but images are provided, use a default prompt
    const finalPrompt = prompt.trim() || (images && images.length > 0 ? "Please analyze these images." : "");
    
    // Always use JSON for messages (no more FormData)
    const payload: ChatRequest = {
      prompt: finalPrompt,
      model,
      mode,
      images
    };

    console.log('Streaming API payload:', {
      prompt: finalPrompt.substring(0, 100) + '...',
      model,
      mode,
      imagesCount: images?.length || 0,
      firstImageLength: images?.[0]?.length || 0,
      firstImagePreview: images?.[0]?.substring(0, 50) + '...' || 'none'
    });

    const response = await fetch(`${API_BASE_URL}ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = ''; // Buffer to accumulate incomplete chunks

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete();
          break;
        }

        // Decode the chunk and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete lines from buffer
        const lines = buffer.split('\n');
        
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';
        
        // Process complete lines
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          // Skip empty lines
          if (!trimmedLine) {
            continue;
          }
          
          // Handle standard SSE format with 'data:' prefix
          if (trimmedLine.startsWith('data: ')) {
            const dataContent = trimmedLine.slice(6); // Don't trim the content, preserve spaces
            
            if (dataContent.trim() === '[DONE]') {
              onComplete();
              return;
            }
            
            // Skip empty data lines
            if (!dataContent.trim()) {
              continue;
            }
            
            try {
              const data = JSON.parse(dataContent) as StreamResponse;
              if (data.message) {
                onMessage(data.message);
              }
            } catch (parseError) {
              // If not JSON, treat as plain text
              // Clean up any remaining 'data:' prefixes that might be in the content
              let cleanContent = dataContent;
              
              // Remove any stray 'data:' prefixes from the beginning (but preserve content after it)
              if (cleanContent.startsWith('data:')) {
                cleanContent = cleanContent.slice(5);
              }
              
              // Send the cleaned content if it's not empty
              if (cleanContent.trim()) {
                onMessage(cleanContent);
              }
            }
          }
          // Handle your custom format: message\tcontent\ttimestamp
          else if (trimmedLine.startsWith('message\t')) {
            const parts = trimmedLine.split('\t');
            if (parts.length >= 2) {
              const messageContent = parts[1];
              if (messageContent) {
                onMessage(messageContent);
              }
            }
          }
          // Handle event declarations (skip these)
          else if (trimmedLine.startsWith('event:')) {
            continue;
          }
          // Handle any other format as direct message
          else {
            onMessage(trimmedLine);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error('Error in streaming chat:', error);
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      onError('Network error - please check your connection');
    } else if (error instanceof Error) {
      onError(error.message);
    } else {
      onError('Failed to send message - please try again');
    }
  }
};